import { Section } from "collections/Section";
import { Loader, LoadingIndicator } from "elements";
import { useUserByIdQuery } from "generated/graphql";
import {
  Link,
  NavLink,
  Route,
  Switch,
  useHistory,
  useLocation,
  useQueryParamString
} from "polyrouter";
import React, { useEffect, useState } from "react";
import { useTranslation } from "utilities/i18n";
import { Layout } from "views/Main/Layouts";
import { DeviceUserSettings } from "../Settings/DeviceUserSettings";
import { UserDeviceTable } from "./UserDeviceTable";
import { UserInformationCards } from "./UserInformationCards";
import { UserNetworkDiagnostics } from "./UserNetworkDiagnostics";
import { usePolyPlusEnabled } from "utilities/polyPlus";
import { generatePath } from "react-router";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export const DeviceUserDetails: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const history = useHistory();
  const routerState = useLocation().state;
  const [deviceId] = useQueryParamString("deviceId");
  const [currentTenantId, setCurrentTenantId] = useState<string | undefined>(
    undefined
  );
  const [firmwareUpdateInfo, setFirmwareUpdateInfo] = useState({
    count: 0,
    loading: true
  });

  const { data: userData, fetching: userFetching } = useUserByIdQuery({
    variables: {
      id
    }
  });

  const { polyPlusEnabled } = usePolyPlusEnabled(tenantId);

  // Mechanism for tracking changes in tenant selection.
  // If our current tenant changes (from something other than the initial 'undefined'),
  // navigate back to the user list page.
  useEffect(() => {
    if (!currentTenantId) {
      setCurrentTenantId(tenantId);
      return;
    }
    if (tenantId !== currentTenantId) {
      history.push("/manage/device-users");
    }
  }, [tenantId, currentTenantId, history, routerState]);

  useEffect(() => {
    if (!deviceId) {
      setFirmwareUpdateInfo(firmwareInfo => ({
        ...firmwareInfo,
        loading: false
      }));
    }
  }, [deviceId, setFirmwareUpdateInfo]);

  if (userFetching) {
    return <LoadingIndicator />;
  }

  const user = userData?.user?.user;

  const { Content } = Layout;
  const { Header, Content: SContent } = Section;
  return user ? (
    <Content scrolling>
      <Loader loading={userFetching}>
        <Content.ScrollingList>
          <Header className="mb-1 d-flex" data-testid="device-users-name">
            {user.name}
            {/* US Remote is hard coded for now until
            we have further detail on where to get the value from  */}
            <div className="page-subheader">&nbsp; | US Remote</div>
          </Header>
          <Section>
            <Header>{t("USER_INFORMATION")}</Header>
            <SContent>
              <UserInformationCards firmwareUpdateInfo={firmwareUpdateInfo} />
            </SContent>
          </Section>
          <Section>
            <Header>{t("ASSOCIATED_DEVICES")}</Header>
            <SContent>
              <UserDeviceTable setFirmwareUpdateInfo={setFirmwareUpdateInfo} />
            </SContent>
          </Section>
          {polyPlusEnabled && (
            <Section className="mt-2 mb-4">
              <Header data-testid="network-diagnostics">
                {t("DEVICE_USAGE_AND_NETWORK_DIAGNOSTICS")}
              </Header>
              <SContent>
                <UserNetworkDiagnostics deviceId={deviceId} />
              </SContent>
            </Section>
          )}
          {/*
          // Device usage components go here when ready.
           */}
        </Content.ScrollingList>
      </Loader>
    </Content>
  ) : null;
};

export const DeviceUserDetailsRouter: React.FC = () => {
  const { t } = useTranslation();
  const [user_id] = useQueryParamString("user_id");
  const tenantId = useTenantId();

  const { data: userData, fetching: userFetching } = useUserByIdQuery({
    variables: {
      id: user_id!
    }
  });

  if (userFetching) {
    return <LoadingIndicator />;
  }
  if (!user_id) {
    return null;
  }

  const user = userData?.user?.user;

  const { Sidebar } = Layout;

  return (
    <>
      <Sidebar>
        {user?.picture && (
          <img src={user.picture} alt="Device user" className="avatar circle" />
        )}
        {user?.email && (
          <Link
            to={generatePath(
              "/admin/accounts/details/:tid/members/details/:user_id",
              { tid: tenantId, user_id: user.user_id || 0 }
            )}
          >
            {user.email}
          </Link>
        )}
        <ul className="menu router">
          <li>
            <NavLink
              nest
              exact
              to={{
                pathname: "",
                search: `user_id=${user_id}`
              }}
            >
              {t("OVERVIEW")}
            </NavLink>
          </li>
          <li>
            {/* Add lens desktop settings here when they're ready */}
            <Route nest path="/settings">
              <ul className="menu router sub">
                <li>
                  <NavLink
                    nest
                    exact
                    to={{
                      pathname: "/device-alerts",
                      search: `user_id=${user_id}`
                    }}
                  >
                    {t("DEVICE_ALERTS")}
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    nest
                    exact
                    to={{
                      pathname: "/software-update",
                      search: `user_id=${user_id}`
                    }}
                  >
                    {t("APP_SOFTWARE_UPDATE")}
                  </NavLink>
                </li>
              </ul>
            </Route>
          </li>
        </ul>
      </Sidebar>
      <Switch>
        <Route nest path="/settings">
          <DeviceUserSettings id={user_id} />
        </Route>
        <Route nest path="/">
          <DeviceUserDetails id={user_id} />
        </Route>
      </Switch>
    </>
  );
};

export default DeviceUserDetailsRouter;
